export enum CustomerType {
  Shipper = 'Shipper',
  Forwarder = 'Forwarder',
  WebUser = 'WebUser',
  Carrier = 'Carrier',
  Platform = 'Platform',
  WebShop = 'WebShop',
  TmsProvider = 'TmsProvider',
  Offset = 'Offset',
  Widget = 'Widget'
}

export enum LicenceType {
  Widget = 'Widget',
  AsTag = 'AsTag',
  Basic = 'Basic',
  Business = 'Business',
  BusinessRiege = 'Business_Riege',
  Partner = 'Partner',
  Reseller = 'Reseller',
  Platform = 'Platform'
}



// Shared configuration/licensing/limits fields used by both Company and
// CreateCompanyData. Extracted to remove duplication; the field set is
// identical between the two interfaces.
export interface CompanyConfigurationBase {
  // Configuration
  customerType?: CustomerType;
  partnerName?: string;

  // Business Information



  // Licensing
  licenceStart?: Date;
  licenceEnd?: Date;
  licenceRenewStart?: Date;
  licenceRenewEnd?: Date;
  licenceType?: LicenceType;
  isDemo?: boolean;
  analytics?: boolean;
  calculationsAllotted?: number;
  calculationsLeft?: number;

  // Limits and Counters
  totalNumCalculations?: number;
  totalNumGeoLocs?: number;
  geolocationAllotted?: number;
  geolocationLeft?: number;
  addedShipments?: number;
  addedGeoLimit?: number;

  // Unique Shipments
  uniqueShipmentsAllotted?: number;
  uniqueShipmentsLeft?: number;
  totalNumUniqueShipments?: number;
  addedUniqueShipments?: number;

  // Features and Status
  isUnlimited?: boolean;
  certificateAvailable?: boolean;
  disabled?: boolean;
  resellerId?: number;
}

export interface Company extends CompanyConfigurationBase {
  // Basic Info
  id: number;
  name: string;
  apiKey: string;
  api_key?: string; // Backend returns snake_case, frontend expects camelCase

  // Address
  addressStreet?: string;
  address_street?: string; // Backend returns snake_case, frontend expects camelCase
  addressCity?: string;
  address_city?: string; // Backend returns snake_case, frontend expects camelCase
  addressPostalCode?: string;
  address_postal_code?: string; // Backend returns snake_case, frontend expects camelCase
  addressCountry?: string;
  address_country?: string; // Backend returns snake_case, frontend expects camelCase
  addressState?: string;
  address_state?: string; // Backend returns snake_case, frontend expects camelCase

  // Business Details
  logoUrl: string;
  logo_url?: string; // Backend returns snake_case, frontend expects camelCase

  isActive: boolean;
  is_active?: boolean; // Backend returns snake_case, frontend expects camelCase
  showLegBreakdown?: boolean; // XML API leg-wise breakdown toggle
  show_leg_breakdown?: boolean; // Backend returns snake_case, frontend expects camelCase

  // Certificate Text Fields
  upperText?: string;
  bottomText?: string;

  // Storage Keys
  dataStorageKey?: string;

  // System Fields
  createdAt: string;
  updatedAt: string;
  userCount?: number;
  shipmentCount?: number;
}

export interface Address {
  street: string;
  city: string;
  postalCode: string;
  country: string;
  state?: string;
}

export interface CreateCompanyData extends CompanyConfigurationBase {
  // Basic Info
  name: string;

  // Address
  addressStreet?: string;
  addressCity?: string;
  addressPostalCode?: string;
  addressCountry?: string;
  addressState?: string;

  // Business Details
  logoUrl: string;

  showLegBreakdown?: boolean; // XML API leg-wise breakdown toggle

  // Certificate Text Fields
  upperText?: string;
  bottomText?: string;

  // Storage Keys
  dataStorageKey?: string;
}

export interface SubCustomer {
  id: number;
  name: string;
  code: string;
  companyId: number;
  company_id?: number; // Backend returns snake_case
  firstName?: string;
  first_name?: string; // Backend returns snake_case
  lastName?: string;
  last_name?: string; // Backend returns snake_case
  userId?: number;
  user_id?: number; // Backend returns snake_case
  profileImageUrl?: string;
  profile_image_url?: string; // Backend returns snake_case
  address?: Address;
  address_street?: string;
  address_city?: string;
  address_postal_code?: string;
  address_country?: string;
  address_state?: string;
  isActive: boolean;
  is_active?: boolean; // Backend returns snake_case
  subCompanyIds?: number[]; // Many-to-many relationship with sub-companies
  sub_company_ids?: number[]; // Backend returns snake_case
  createdAt: string;
  created_at?: string; // Backend returns snake_case
  updatedAt: string;
  updated_at?: string; // Backend returns snake_case
}

export interface CreateSubCustomerData {
  name: string;
  code: string;
  companyId: number;
  firstName?: string;
  lastName?: string;
  profileImageUrl?: string;
  address?: Address;
  subCompanyIds?: number[]; // Many-to-many relationship with sub-companies
}

export interface SubCompany {
  id: number;
  companyId: number;
  company_id?: number; // Backend returns snake_case
  name: string;
  code?: string;
  description?: string;
  isActive: boolean;
  is_active?: boolean; // Backend returns snake_case
  createdAt: string;
  created_at?: string; // Backend returns snake_case
  updatedAt: string;
  updated_at?: string; // Backend returns snake_case
  companyName?: string; // When joined with companies table
  company_name?: string; // Backend returns snake_case
}

export interface CreateSubCompanyData {
  name: string;
  code: string;
  companyId: number;
  description?: string;
}

export interface Branch {
  id: number;
  companyId: number;
  company_id?: number; // Backend returns snake_case
  subCompanyId: number;
  sub_company_id?: number; // Backend returns snake_case
  name: string;
  code?: string;
  address?: string;
  city?: string;
  country?: string;
  description?: string;
  isActive: boolean;
  is_active?: boolean; // Backend returns snake_case
  createdAt: string;
  created_at?: string; // Backend returns snake_case
  updatedAt: string;
  updated_at?: string; // Backend returns snake_case
  companyName?: string; // When joined with companies table
  company_name?: string; // Backend returns snake_case
  subCompanyName?: string; // When joined with sub_companies table
  sub_company_name?: string; // Backend returns snake_case
}

export interface CreateBranchData {
  name: string;
  code?: string;
  companyId: number;
  subCompanyId: number;
  address?: string;
  city?: string;
  country?: string;
  description?: string;
}